import React, { useState } from "react";
import { ChevronDown, Copy, Check, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { AssetTile } from "@/components/marketing/AssetTile";
import { KIND_LABELS, fmtDate } from "@/lib/marketing";

const CopyBtn = ({ value, testid }) => {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setOk(true);
    toast.success("Testo copiato");
    setTimeout(() => setOk(false), 1800);
  };
  return (
    <button onClick={copy} className="vg-btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs" data-testid={testid}>
      {ok ? <Check size={13} /> : <Copy size={13} />} {ok ? "Copiato" : "Copia"}
    </button>
  );
};

const Body = ({ item, assetBase }) => (
  <div className="px-5 pb-5 space-y-5 border-t border-gray-100 pt-4">
    {item.summary && <p className="text-sm text-gray-600 break-words">{item.summary}</p>}
    {item.texts?.map((t, i) => (
      <div key={i} className="bg-[#FAFAFA] border border-gray-100 rounded-[10px] p-4" data-testid={`material-text-${item.id}-${i}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{t.label}</span>
          <CopyBtn value={t.body} testid={`material-copy-${item.id}-${i}`} />
        </div>
        <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap break-words" data-testid={`material-text-body-${item.id}-${i}`}>{t.body}</p>
      </div>
    ))}
    {item.assets?.length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {item.assets.map((a) => <AssetTile key={a.id} asset={a} basePath={assetBase} />)}
      </div>
    )}
    {item.videos?.map((v, i) => (
      <div key={i} className="flex items-center justify-between gap-3 border border-gray-200 rounded-[10px] p-3" data-testid={`material-video-${item.id}-${i}`}>
        <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-[#B40101] truncate">
          <PlayCircle size={18} /> {v.label}
        </a>
        <CopyBtn value={v.url} testid={`material-video-copy-${item.id}-${i}`} />
      </div>
    ))}
    {item.instructions && (
      <div data-testid={`material-instructions-${item.id}`}>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Istruzioni</p>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.instructions}</p>
      </div>
    )}
  </div>
);

export const MaterialCard = ({ item, assetBase, onOpen, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    if (!open && onOpen) onOpen(item);
    setOpen(!open);
  };
  return (
    <div className="vg-card overflow-hidden" data-testid={`material-${item.id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#FAFAFA] transition-colors" data-testid={`material-toggle-${item.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#B40101]">{KIND_LABELS[item.kind]}</span>
            {item.is_new && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#B40101] text-white" data-testid={`material-new-${item.id}`}>Nuovo</span>}
          </div>
          <h3 className="font-bold text-[#0A0A0A] truncate">{item.title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{fmtDate(item.publish_at)}</p>
        </div>
        <ChevronDown size={18} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <Body item={item} assetBase={assetBase} />}
    </div>
  );
};
